import React, { useState, useContext } from 'react';
import { AuthContext } from '../AuthContext';
import { authenticatedFetch, API_ENDPOINT } from '../utils/api';
import { Upload, FileText, Activity, X } from 'lucide-react';
import toast from 'react-hot-toast';
import FileDownloadButton from '../FileDownloadButton';
import { FormField, FormSelect, Widget, Pagination } from './Common';
import HistoricAnalysisModal from './HistoricAnalysisModal';

function HistoricDataManagement({ onUploadComplete, reports }) {
  const { organization } = useContext(AuthContext);
  const [formData, setFormData] = useState({
    incident_name: '',
    incident_date: '',
    hazard_type: '',
    incident_type: 'historic'
  });

  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [cancelling, setCancelling] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10); // Show 10 reports per page

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!organization?.organizationId) {
      toast.error('Organization not loaded. Please refresh the page.');
      return;
    }

    if (files.length === 0) {
      toast.error('Please select at least one file');
      return;
    }
    
    setUploading(true);
    const uploadToast = toast.loading('Uploading historic files...');

    try {
      const filePromises = files.map(file => {
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            resolve({
              filename: file.name,
              file_type: file.name.split('.').pop().toLowerCase(),
              content: reader.result.split(',')[1]
            });
          };
          reader.readAsDataURL(file);
        });
      });

      const encodedFiles = await Promise.all(filePromises);

      const response = await fetch(`${API_ENDPOINT}/upload-historic`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          ...formData, 
          files: encodedFiles,
          organization_id: organization.organizationId
        })
      });

      const result = await response.json();

      if (response.ok) {
        toast.success('Historic AAR uploaded and processing started!', { id: uploadToast });
        setTimeout(() => {
          onUploadComplete();
          setFormData({ incident_name: '', incident_date: '', hazard_type: '', incident_type: 'historic' });
          setFiles([]);
        }, 2000);
      } else {
        toast.error(`Error: ${result.error}`, { id: uploadToast });
      }
    } catch (error) {
      toast.error(`Error: ${error.message}`, { id: uploadToast });
    }

    setUploading(false);
  };

  const handleViewAnalysis = (reportId) => {
    setSelectedReport(reportId);
  };

  const handleDeleteReport = async (aarId) => {
  if (!window.confirm('Delete this historic report? This cannot be undone.')) {
    return;
  }

  const deleteToast = toast.loading('Deleting report...');

  try {
    const response = await authenticatedFetch(
  `${API_ENDPOINT}/dashboard/reports/${aarId}?organization_id=${organization?.organizationId}`,
  { method: 'DELETE' }
);

    if (response.ok) {
      toast.success('Report deleted successfully', { id: deleteToast });
      onUploadComplete();
    } else {
      toast.error('Failed to delete report', { id: deleteToast });
    }
  } catch (error) {
    console.error('Error deleting report:', error);
    toast.error('Error deleting report', { id: deleteToast });
  }
};

  const handleCancelProcessing = async (aarId) => {
    if (!window.confirm('Cancel AAR generation? This will stop the AI analysis in progress.')) {
      return;
    }

    setCancelling(aarId);
    const cancelToast = toast.loading('Cancelling processing...');
    
    try {
      const data = await authenticatedFetch(
        `${API_ENDPOINT}/dashboard/reports/${aarId}/cancel?organization_id=${organization?.organizationId}`,
        { method: 'POST' }
      );

      toast.success('Processing cancelled successfully', { id: cancelToast });
      await onUploadComplete();
      
    } catch (error) {
      console.error('Error cancelling processing:', error);
      toast.error(`Error cancelling processing: ${error.message}`, { id: cancelToast });
    } finally {
      setCancelling(null);
    }
  };

  const getStatusBadge = (status) => {
    const configs = {
      completed: { color: 'bg-green-100 text-green-800', label: 'Completed' },
      processing: { color: 'bg-yellow-100 text-yellow-800', label: 'Processing...' },
      cancelled: { color: 'bg-gray-100 text-gray-800', label: 'Cancelled' },
      failed: { color: 'bg-red-100 text-red-800', label: 'Failed' }
    };
    const config = configs[status?.toLowerCase()] || configs.processing;
    return <span className={`px-2 py-1 rounded text-xs font-medium ${config.color}`}>{config.label}</span>;
  };

  // Calculate pagination
  const totalItems = reports.length;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedReports = reports.slice(startIndex, endIndex);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Upload Form */}
      <Widget 
        title="Upload Historic AARs" 
        subtitle="Add past after-action reports for AI analysis and trend tracking"
      >
        {/* Info Banner */}
        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">About Historic Uploads</h3>
              <div className="mt-2 text-sm text-blue-700">
                <p className="mb-2">
                  <strong>Historic uploads</strong> are analyzed by AI and included in all analytics:
                </p>
                <ul className="list-disc list-inside space-y-1">
                  <li>AI analysis of uploaded documents</li>
                  <li>Capability gaps and trend identification</li>
                  <li>Historical comparisons and insights</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField
              label="Incident/Event Name"
              required
              value={formData.incident_name}
              onChange={(e) => setFormData({ ...formData, incident_name: e.target.value })}
              placeholder="e.g., 2022 Flood Response"
            />
            <FormField
              label="Incident/Event Date"
              type="date"
              required
              value={formData.incident_date}
              onChange={(e) => setFormData({ ...formData, incident_date: e.target.value })}
            />
            <FormSelect
              label="Incident Type"
              required
              value={formData.incident_type}
              onChange={(e) => setFormData({ ...formData, incident_type: e.target.value })}
              options={[
                { value: 'historic', label: 'Historic Record' },
                { value: 'incident', label: 'Past Incident' },
                { value: 'exercise', label: 'Past Exercise' },
                { value: 'training', label: 'Past Training' },
                { value: 'drill', label: 'Past Drill' }
              ]}
            />
            <FormSelect
              label="Hazard Type"
              required
              value={formData.hazard_type}
              onChange={(e) => setFormData({ ...formData, hazard_type: e.target.value })}
              options={[
                { value: '', label: 'Select hazard...' },
                { value: 'natural', label: 'Natural Disaster' },
                { value: 'technological', label: 'Technological' },
                { value: 'biological', label: 'Biological' },
                { value: 'chemical', label: 'Chemical' },
                { value: 'radiological', label: 'Radiological' },
                { value: 'cyber', label: 'Cyber' },
                { value: 'terrorism', label: 'Terrorism' },
                { value: 'mass_gathering', label: 'Mass Gathering' },
                { value: 'other', label: 'Other' }
              ]}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Upload Historic Documents <span className="text-red-500">*</span>
            </label>
            <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg hover:border-blue-400 transition">
              <div className="space-y-1 text-center">
                <Upload className="mx-auto h-12 w-12 text-gray-400" />
                <div className="flex text-sm text-gray-600">
                  <label className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500">
                    <span>Upload historic files</span>
                    <input
                      type="file"
                      multiple
                      onChange={(e) => setFiles(Array.from(e.target.files))}
                      className="sr-only"
                      accept=".pdf,.doc,.docx,.txt"
                    />
                  </label>
                  <p className="pl-1">or drag and drop</p>
                </div>
                <p className="text-xs text-gray-500">PDF, Word, or text documents</p>
              </div>
            </div>
            {files.length > 0 && (
              <div className="mt-4 space-y-2">
                <p className="text-sm font-medium text-gray-700">
                  {files.length} file(s) selected:
                </p>
                {files.map((file, index) => (
                  <div key={index} className="flex items-center justify-between text-sm bg-gray-50 p-3 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <FileText className="w-4 h-4 text-gray-400" />
                      <span className="flex-1 font-medium">{file.name}</span>
                      <span className="text-gray-400">({(file.size / 1024).toFixed(1)} KB)</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setFiles(files.filter((_, i) => i !== index))}
                      className="text-red-500 hover:text-red-700 p-1"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center justify-between pt-4 border-t border-gray-200">
            <button
              type="submit"
              disabled={uploading || files.length === 0}
              className={`px-6 py-3 rounded-lg text-white font-medium transition flex items-center space-x-2 ${
                uploading || files.length === 0 ? 'bg-gray-400 cursor-not-allowed' : 'bg-purple-600 hover:bg-purple-700'
              }`}
            >
              {uploading ? (
                <>
                  <Activity className="w-4 h-4 animate-spin" />
                  <span>Uploading...</span>
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  <span>Upload Historic AAR</span>
                </>
              )}
            </button>
          </div>
        </form>
      </Widget>

      {/* Historic Reports Table */}
      <Widget title={`Your Historic AARs (${reports.length})`}>
        {reports.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-600">No historic AARs uploaded yet</p>
            <p className="text-sm text-gray-500 mt-2">Upload past reports above to include them in your analytics</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Files</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {paginatedReports.map((report) => (
                    <tr key={report.aar_id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium">{report.incident_name || 'Untitled'}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {new Date(report.incident_date || report.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{report.incident_type || 'Historic'}</td>
                      
                      {/* Files Column with Download Buttons */}
                      <td className="px-4 py-3">
                        {report.uploaded_files && report.uploaded_files.length > 0 ? (
                          <div className="space-y-1">
                            {report.uploaded_files.map((file, fileIndex) => (
                              <div key={fileIndex} className="flex items-center space-x-2">
                                <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                <span className="text-xs text-gray-600 truncate max-w-[150px]" title={file.original_filename || file.filename}>
                                  {file.original_filename || file.filename || 'Document'}
                                </span>
                                <FileDownloadButton 
                                  s3Key={file.s3_key}
                                  filename={file.original_filename || file.filename || 'download'}
                                  className="text-xs py-1 px-2"
                                />
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">No files</span>
                        )}
                      </td>

                      <td className="px-4 py-3">{getStatusBadge(report.status)}</td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          {report.status === 'completed' && (
                            <button
                              onClick={() => handleViewAnalysis(report.aar_id)}
                              className="px-3 py-1 bg-blue-100 text-blue-700 rounded text-sm hover:bg-blue-200 transition"
                            >
                              View Analysis
                            </button>
                          )}
                          {report.status === 'processing' && (
                            <button
                              onClick={() => handleCancelProcessing(report.aar_id)}
                              disabled={cancelling === report.aar_id}
                              className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded text-sm hover:bg-yellow-200 transition disabled:opacity-50"
                            >
                              {cancelling === report.aar_id ? 'Cancelling...' : 'Cancel'}
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteReport(report.aar_id)}
                            className="px-3 py-1 bg-red-100 text-red-700 rounded text-sm hover:bg-red-200 transition"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination Component */}
            <Pagination
              currentPage={currentPage}
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
            />
          </>
        )}
      </Widget>

      {/* Analysis Popup Modal */}
      {selectedReport && (
        <HistoricAnalysisModal 
          reportId={selectedReport} 
          onClose={() => setSelectedReport(null)} 
        />
      )}
    </div>
  );
}

export default HistoricDataManagement;